import { useMutation } from '@apollo/client';
import { AnyAsset, isPlentyGoalAccount } from '@common/financialEntities';
import { SetRetirementGoalAssetsDocument } from '@common/graphql/generated';
import {
    assetsFromFEs,
    useFinancialEntitiesContext,
} from '@common/react/financialEntities';
import { PartnerSwitcherType } from '@common/react/partnerships';
import { BeneficiaryRelationship, IraType } from '@common/types';
import { showIncludedAccountsModal } from './IncludedAccountsModal';
import { useRetirementGoalContext } from './RetirementGoalDetailContainer';

export const labelByIraType: Record<IraType, string> = {
    TRADITIONAL: 'Traditional',
    ROTH: 'Roth',
};

export const labelByRelationship: Record<BeneficiaryRelationship, string> = {
    brother: 'Brother',
    daughter: 'Daughter',
    father: 'Father',
    father_in_law: 'Father-in-law',
    grandfather: 'Grandfather',
    grandmother: 'Grandmother',
    granddaughter: 'Granddaughter',
    grandson: 'Grandson',
    mother: 'Mother',
    mother_in_law: 'Mother-in-law',
    nephew: 'Nephew',
    niece: 'Niece',
    self: 'Self',
    sister: 'Sister',
    son: 'Son',
    spouse: 'Spouse',
};

export const getIncludedAccounts = (
    assets: AnyAsset[] | undefined | null,
    goalId: string,
    partnerSwitcherType: PartnerSwitcherType
) =>
    (assets ?? [])
        .filter((fe) => fe.goalId === goalId)
        .filter((fe) =>
            partnerSwitcherType === 'MINE'
                ? fe.isMine
                : partnerSwitcherType === 'THEIRS'
                  ? !fe.isMine
                  : true
        ) ?? [];

export const getMyIncludableAccounts = (assets?: AnyAsset[]) =>
    (assets ?? []).filter((fe) => fe.isMine) ?? [];

export const useOnCompleteHandlers = () => {
    const [setRetirementGoalAssets] = useMutation(
        SetRetirementGoalAssetsDocument
    );
    const assets = useAssets();
    const { goal } = useRetirementGoalContext();

    return {
        onCompleteConnection: () => {
            // Return to "Which accounts" modal
            showIncludedAccountsModal({});
        },
        onCompleteManual: async (feId: string) => {
            // Intent was to add this account
            // to the Retirement goal, so
            // automatically add it
            await setRetirementGoalAssets({
                variables: {
                    goalId: goal.id,
                    financialEntityIds: [
                        ...getIncludedAccounts(assets, goal.id, 'MINE').map(
                            ({ id }) => id
                        ),
                        feId,
                    ],
                },
            });
        },
    };
};

export const useAssets = () => {
    const { fes } = useFinancialEntitiesContext();

    return assetsFromFEs(fes.data?.financialEntities ?? []).filter(
        (fe) => !isPlentyGoalAccount(fe)
    );
};

export const mapEqualFractions = <T extends { fraction: number }>(
    value: T,
    index: number,
    array: T[]
): T => {
    return {
        ...value,
        fraction: equalFractionsByCount[array.length][index] ?? 0,
    };
};

const equalFractionsByCount: Record<number, (number | undefined)[]> = {
    1: [1],
    2: [0.5, 0.5],
    3: [0.34, 0.33, 0.33],
    4: [0.25, 0.25, 0.25, 0.25],
    5: [0.2, 0.2, 0.2, 0.2, 0.2],
    6: [0.17, 0.17, 0.17, 0.17, 0.16, 0.16],
    7: [0.15, 0.15, 0.14, 0.14, 0.14, 0.14, 0.14],
    8: [0.13, 0.13, 0.13, 0.13, 0.12, 0.12, 0.12, 0.12],
    9: [0.12, 0.11, 0.11, 0.11, 0.11, 0.11, 0.11, 0.11, 0.11],
    10: [0.1, 0.1, 0.1, 0.1, 0.1, 0.1, 0.1, 0.1, 0.1, 0.1],
};
